import React, { useState, useEffect } from "react";
import moment from "moment";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { useFilterContext } from "../context/FilterContext";
import Navbar from "./Navbar.js";
import CategoryFilter from "./CategoryFilter.js";
import "./layout.css";
import TicketTable from "./ticket-table/TicketTable.js";
import { useTicketContext } from "../context/TicketContext.js";

// Columns exported to Excel. Mirrors the visible ticket-table grid (minus
// per-row UI affordances like View / Action that aren't meaningful in a
// spreadsheet). Date stays as a parsed value so Excel treats it as a date.
const EXPORT_COLUMNS = [
  { header: "Ticket", get: (r) => r.ticketid || "" },
  { header: "Title", get: (r) => r.title || r.workflowtype || "" },
  { header: "Subject", get: (r) => r.subject || "" },
  { header: "Status", get: (r) => r.displayStatus || r.status || "" },
  { header: "Doc Class", get: (r) => r.docClass || "" },
  { header: "Created", get: (r) => (r.creationdate ? new Date(r.creationdate) : "") },
  { header: "Check Number", get: (r) => r.checkNumber || "" },
  { header: "Check Type", get: (r) => r.checkType || "" },
  { header: "Check Amount", get: (r) => r.checkAmount || "" },
  { header: "Comments", get: (r) => r.message || "" },
];

function exportTicketsToExcel(rows) {
  const data = rows.map((row) => {
    const obj = {};
    EXPORT_COLUMNS.forEach(({ header, get }) => {
      obj[header] = get(row);
    });
    return obj;
  });
  const ws = XLSX.utils.json_to_sheet(data, {
    header: EXPORT_COLUMNS.map((c) => c.header),
  });
  // Auto-fit column widths (approx) based on header + first-row content
  ws["!cols"] = EXPORT_COLUMNS.map(({ header, get }) => {
    const headerLen = header.length;
    const sample = data[0] ? String(get(data[0]) || "").length : 0;
    return { wch: Math.min(40, Math.max(12, headerLen, sample)) };
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Tickets");
  const stamp = moment().format("YYYYMMDD_HHmmss");
  XLSX.writeFile(wb, `tickets_${stamp}.xlsx`);
}

const checkDateRange = (creationdate, dateRangeValue) => {
  if (!dateRangeValue) return true;

  const ticketDate = moment.utc(creationdate);
  if (!ticketDate.isValid()) return false;

  const now = moment.utc();

  if (dateRangeValue === "today") {
    return ticketDate.isSame(now, "day");
  } else if (dateRangeValue === "last7") {
    return ticketDate.isAfter(now.clone().subtract(7, "days").startOf("day"));
  } else if (dateRangeValue === "last30") {
    return ticketDate.isAfter(now.clone().subtract(30, "days").startOf("day"));
  } else if (dateRangeValue.startsWith("custom:")) {
    const parts = dateRangeValue.split(":");
    const from = moment.utc(parts[1]).startOf("day");
    const to = moment.utc(parts[2]).endOf("day");
    return ticketDate.isBetween(from, to, null, "[]");
  }

  return true;
};

const Layout = ({ token }) => {
  const { loading, tickets, getTickets } = useTicketContext();
  const { selectedCategories, updateFilters } = useFilterContext();
  const [refTaskList, setRefTaskList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();

  const CategoryRef = {
    Assignees: "assignedto",
    Statuses: "displayStatus",
    TicketType: "workflowtype",
    SubStatus: "subStatus",
    Source: "source",
    "Sort by": "Sortby",
  };

  const fetchTickets = async () => {
    await getTickets();
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  useEffect(() => {
    if (tickets) {
      const filteredTasks = filterAndSearchTasks();
      setRefTaskList(filteredTasks);
    }
  }, [tickets, selectedCategories, searchQuery]);

  const filterAndSearchTasks = () => {
    if (!tickets) return [];
    const filtersByCategory = selectedCategories.reduce((acc, filter) => {
      const [category, ...rest] = filter.split(":");
      const value = rest.join(":");
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(value);
      return acc;
    }, {});

    const dateRangeValue = filtersByCategory["DateRange"]?.[0];
    delete filtersByCategory["DateRange"];

    return tickets.filter((task) => {
      task['checkNumber'] = task.metadata && task.metadata.CheckNumber
      task['checkAmount'] = (task.metadata && task.metadata.Amount)
      task['CheckType'] = (task.metadata && task.metadata.CheckType)
      const rawSource = task.metadata?.source;
      if (rawSource) {
        const emailMatch = rawSource.match(/<([^>]+)>/);
        task['source'] = emailMatch ? emailMatch[1] : rawSource;
      } else {
        task['source'] = rawSource;
      }
      // Compute subStatus - use metadata.subStatus if available, otherwise fall back to displayStatus
      task['subStatus'] = task.metadata?.subStatus || task.displayStatus;

      const matchesFilters = Object.entries(filtersByCategory).every(
        ([category, values]) =>
          values.some((value) => task[CategoryRef[category]] === value)
      );

      const matchesDate = checkDateRange(task.creationdate, dateRangeValue);

      const matchesSearch =
        searchQuery === "" ||
        Object.values(task).some((value) =>
          String(value).toLowerCase().includes(searchQuery.toLowerCase())
        );

      return matchesFilters && matchesDate && matchesSearch;
    });
  };

  return (
    <div className="layout-container bg-white footer-height">
      <Navbar />

      <div className="flex">
        <div className="w-348 h-screen">
          <CategoryFilter data={tickets} onFilterChange={updateFilters} />
        </div>

        {loading ? (
          <div className="flex w-full justify-center h-screen">
            <p className="loading-custom w-full mt-10 mb-10 py-10 mx-4 bg-background-3 h-50 flex items-center justify-center rounded-md">
              loading...
            </p>
          </div>
        ) : (
          <div className="w-full p-10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <h1 className="font-28-42-400 poppins-regular">
                  Tickets({refTaskList?.length ?? 0})
                </h1>
                <button
                  onClick={() => navigate("/manage-ticket/create-smc")}
                  className="smc-create-cta"
                  type="button"
                >
                  + Create Ticket
                </button>
              </div>
              <div className="search-container flex items-center gap-2">
                <input
                  type="text"
                  className="search-bar border rounded-md px-4 py-2"
                  placeholder="Search tickets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const filteredTasks = filterAndSearchTasks();
                      setRefTaskList(filteredTasks);
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => exportTicketsToExcel(refTaskList)}
                  disabled={!refTaskList || refTaskList.length === 0}
                  className="border rounded-md px-4 py-2 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Download the current filtered tickets as an Excel file"
                >
                  Export to Excel
                </button>
              </div>
            </div>

            <TicketTable data={refTaskList} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Layout;
